import DownloadIcon from '@mui/icons-material/Download';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { findEntitySpec } from '../../models/EntitySpecs';
import { api } from '../../services/Server';
import { formatApiError } from '../../utils/Error';

import ConfirmDialog from './ConfirmDialog';
import Detail from './Detail';
import Editor from './Editor';
import ExportSuppliersDialog from './ExportSuppliersDialog';
import { displayName } from './helpers';
import Sidebar from './Sidebar';
import type { AnyEntity, ConfirmState } from './types';

const EmptyDetail = () => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      textAlign: 'center',
      p: 4,
    }}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '16px',
        bgcolor: '#E8F4FD',
        color: '#0087C3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 32 }} />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, color: '#002A5C' }}>
      Select an item
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.5 }}>
      Pick an entry from the list to view its details.
    </Typography>
  </Box>
);

const matchesFilters = (item: AnyEntity, search: string, countries: string[]): boolean => {
  if (search) {
    const haystack = Object.values(item)
      .filter((v) => v !== null && v !== undefined)
      .map((v) => String(v).toLowerCase())
      .join(' ');
    if (!haystack.includes(search)) {
      return false;
    }
  }
  if (countries.length) {
    const ctry = String(item.country ?? '').toUpperCase();
    if (!countries.includes(ctry)) return false;
  }
  return true;
};

const compareByName = (a: AnyEntity, b: AnyEntity): number => {
  const an = String(a.name ?? `#${a.id}`);
  const bn = String(b.name ?? `#${b.id}`);
  return an.localeCompare(bn, undefined, { sensitivity: 'base' });
};

const DatabaseInner = ({ entityKey }: { entityKey: string }) => {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const spec = useMemo(() => findEntitySpec(entityKey), [entityKey]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const sel = searchParams.get('selected');
    return sel ? Number(sel) : null;
  });
  const [tab, setTab] = useState(() => {
    const t = searchParams.get('tab');
    return t ? Number(t) : 0;
  });
  const [subtab, setSubtab] = useState<number | null>(() => {
    const s = searchParams.get('subtab');
    return s ? Number(s) : null;
  });
  const [editing, setEditing] = useState<Partial<AnyEntity> | null>(null);
  const [editorGroup, setEditorGroup] = useState<string | undefined>(undefined);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ConfirmState | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Keep URL search params in sync with selectedId, tab, and subtab.
  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (selectedId !== null) next.set('selected', String(selectedId));
      else next.delete('selected');
      if (tab > 0) next.set('tab', String(tab));
      else next.delete('tab');
      if (subtab !== null && tab > 0) next.set('subtab', String(subtab));
      else next.delete('subtab');
      return next;
    }, { replace: true });
  }, [selectedId, tab, subtab, setSearchParams]);

  const handleSelectItem = (id: number) => {
    setSelectedId(id);
    setTab(0); // reset tab when switching items
    setSubtab(null);
  };

  const handleTabChange = (t: number) => {
    setTab(t);
    setSubtab(null); // reset subtab when switching tabs
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['list', spec?.path],
    queryFn: () => api.list<AnyEntity>(spec!.path),
    enabled: !!spec,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    // Keep the sidebar in stable alphabetical order so edits don't jump entries
    // to the bottom of the list (the API returns rows in update order).
    return data.filter((d) => matchesFilters(d, q, countries)).slice().sort(compareByName);
  }, [data, search, countries]);

  // Auto-select the first item once a list arrives.
  useEffect(() => {
    if (selectedId === null && filtered.length > 0) setSelectedId(filtered[0].id);
  }, [filtered, selectedId]);

  const selectedListItem = filtered.find((d) => d.id === selectedId) ?? null;

  // The list endpoint trims heavy/text fields for speed; fetch the full
  // record for the selected item so the detail view has everything.
  const { data: selectedFull } = useQuery({
    queryKey: ['get', spec?.path, selectedId],
    queryFn: () => api.get<AnyEntity>(spec!.path, selectedId!),
    enabled: !!spec && selectedId !== null,
  });
  const selected = selectedFull ?? selectedListItem;

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.remove(spec!.path, id),
    onSuccess: (_void, id) => {
      toast.success(`${spec!.singular} deleted`);
      setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ['list', spec!.path] });
      queryClient.removeQueries({ queryKey: ['get', spec!.path, id] });
    },
    onError: (e) => toast.error(formatApiError(e, 'Delete failed')),
  });

  if (!spec) return <Navigate to="/database/entities/suppliers" replace />;

  const askDelete = (item: AnyEntity) => {
    const label = displayName(item, spec.singular);
    setConfirmDelete({
      title: `Delete ${spec.singular}?`,
      message: `“${label}” will be permanently removed. This action cannot be undone.`,
      onConfirm: () => deleteMutation.mutate(item.id),
    });
  };

  const openEditor = (initial: Partial<AnyEntity> | null, groupFilter?: string) => {
    setEditing(initial);
    setEditorGroup(groupFilter);
    setEditorOpen(true);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
        gap: 2,
        p: 2,
        height: 'calc(100vh - 60px)',
        overflow: 'hidden',
      }}
    >
      <Sidebar
        spec={spec}
        items={filtered}
        selectedId={selectedId}
        onSelect={handleSelectItem}
        onNew={() => openEditor({})}
        search={search}
        onSearchChange={setSearch}
        countries={countries}
        onCountriesChange={setCountries}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        extraAction={
          spec.key === 'suppliers' && can('export') ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => setExportOpen(true)}
            >
              Export
            </Button>
          ) : undefined
        }
      />

      <Box sx={{ overflow: 'auto', minHeight: 0, pr: 0.5 }}>
        {selected ? (
          <Detail
            spec={spec}
            item={selected}
            tab={tab}
            onTabChange={handleTabChange}
            subtab={subtab}
            onSubtabChange={setSubtab}
            onEdit={(group) => openEditor(selected, group)}
            onDelete={() => askDelete(selected)}
          />
        ) : (
          <EmptyDetail />
        )}
      </Box>

      {editorOpen && (
        <Editor
          spec={spec}
          initial={editing}
          groupFilter={editorGroup}
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['list', spec.path] });
            if (selectedId !== null) {
              queryClient.invalidateQueries({ queryKey: ['get', spec.path, selectedId] });
            }
          }}
        />
      )}
      <ConfirmDialog state={confirmDelete} onClose={() => setConfirmDelete(null)} />
      {spec.key === 'suppliers' && (
        <ExportSuppliersDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      )}
    </Box>
  );
};

const Database = () => {
  const { entityKey } = useParams();
  if (!entityKey) return <Navigate to="/database/entities/suppliers" replace />;
  // Force a fresh subtree on entity change so all internal state resets cleanly.
  return <DatabaseInner key={entityKey} entityKey={entityKey} />;
};

export default Database;
